import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import {
  ArrowLeft,
  AlertTriangle,
  ExternalLink,
  FileText,
  Info,
  Tag,
  BookOpen,
  Edit,
  Save,
  X,
  Check,
  RefreshCw,
  Plus,
  Globe,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// API and context imports
import { useAuth } from "@/hooks/useAuth";

// Type imports
import { UserStatus, MissingArticlesProps } from "@/types/missing_articles";

// Extracted module imports
import {
  ALL_POSSIBLE_LANGUAGES,
  languageMap,
  defaultMarkdownProps,
} from "@/pages/MissingArticle/types";

import {
  StreamingContentDisplay,
  StreamingThoughtsDisplay,
} from "./MissingArticle/animations";

import {
  useArticleDetails,
  useTitleEditing,
  useStatusManagement,
  useDisplaySettings,
  useGenerationModal,
  useTranslation,
  useModalManagement,
  useWebSocketMessages,
} from "./MissingArticle/hooks";

import {
  GenerationService,
  TranslationService,
} from "./MissingArticle/services";

import {
  DetailItem,
  getSectionIcon,
  VersionSelector,
  LanguageSelector,
  ContentDisplayHeader,
  LoadingSpinner,
  ErrorDisplay,
  FileUploadItem,
} from "./MissingArticle/components";

// AI_GOOD: Main component preserving exact interface and behavior
const MissingArticle: React.FC<MissingArticlesProps> = ({ apiBaseUrl }) => {
  // AI_GOOD: URL and navigation state
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();

  // AI_GOOD: Guard against null token
  const authToken = token || "";

  // AI_GOOD: Tab state preserved exactly
  const [activeTab, setActiveTab] = useState<"details" | "generated">(
    "details",
  );

  // AI_GOOD: Initialize services
  const generationService = new GenerationService(apiBaseUrl, authToken);
  const translationService = new TranslationService(apiBaseUrl, authToken);

  // AI_GOOD: Extract all state through custom hooks
  const { articleDetails, loading, error, fetchArticleDetailsData } =
    useArticleDetails(itemId, apiBaseUrl, authToken);

  const {
    titleOverride,
    isEditing,
    setIsEditing,
    renderEditor,
    animateEditorVisible,
    handleTitleOverride,
    handleTitleRevert,
  } = useTitleEditing(
    articleDetails,
    apiBaseUrl,
    authToken,
    fetchArticleDetailsData,
  );

  const {
    isEditingStatus,
    setIsEditingStatus,
    editableUserStatus,
    setEditableUserStatus,
    editableUserNotes,
    setEditableUserNotes,
    statusUpdateError,
    setStatusUpdateError,
    isUpdatingStatus,
    handleStatusUpdate,
  } = useStatusManagement(
    articleDetails,
    itemId,
    apiBaseUrl,
    authToken,
    fetchArticleDetailsData,
  );

  const {
    selectedDisplayLanguage,
    setSelectedDisplayLanguage,
    selectedDisplayVersion,
    setSelectedDisplayVersion,
    allUniqueVersionsSorted,
    isLanguageEnabledForVersion,
    currentDisplayDoc,
  } = useDisplaySettings(articleDetails, activeTab);

  const {
    isGenerateModalOpen,
    setIsGenerateModalOpen,
    selectedGenLanguage,
    setSelectedGenLanguage,
    setUserPromptAddition,
    userPromptRef,
    getCurrentUserPrompt,
    uploadedFiles,
    setUploadedFiles,
    isGenerating,
    setIsGenerating,
    setGenerationError,
    streamingContent,
    setStreamingContent,
    streamingThoughts,
    setStreamingThoughts,
    enableUrlContext,
    setEnableUrlContext,
    enableGoogleSearch,
    setEnableGoogleSearch,
    isStreamingToArticleArea,
    setIsStreamingToArticleArea,
    isThoughtsAccordionOpen,
    setIsThoughtsAccordionOpen,
    setIsThinkingComplete,
    setGenerationUserMessage,
    setGenerationSuccessMessage,
    setGenerationWarningMessage,
    currentThinkingSection,
    setCurrentThinkingSection,
    fileInputRef,
    handleFileChange,
    removeUploadedFile,
    debouncedSyncHandler,
  } = useGenerationModal();

  const { pendingTranslations, setPendingTranslations } = useTranslation();

  const {
    modalImageUrl,
    isPromptModalOpen,
    setIsPromptModalOpen,
    promptModalContent,
    setPromptModalContent,
    imageModalRef,
  } = useModalManagement();

  // AI_GOOD: WebSocket integration preserving exact message processing
  useWebSocketMessages(
    itemId,
    fetchArticleDetailsData,
    setStreamingContent,
    setStreamingThoughts,
    setGenerationUserMessage,
    setGenerationError,
    setGenerationSuccessMessage,
    setIsGenerating,
    setIsStreamingToArticleArea,
    setIsThoughtsAccordionOpen,
    setIsThinkingComplete,
    setActiveTab,
    setSelectedDisplayVersion,
    setSelectedDisplayLanguage,
    setCurrentThinkingSection,
  );

  // AI_GOOD: Load article details on mount
  useEffect(() => {
    fetchArticleDetailsData();
  }, [fetchArticleDetailsData]);

  // AI_GOOD: Close generation modal when switching tabs
  useEffect(() => {
    if (activeTab === "details" && isGenerateModalOpen) {
      setIsGenerateModalOpen(false);
    }
  }, [activeTab, isGenerateModalOpen, setIsGenerateModalOpen]);

  // AI_GOOD: Event handlers preserved exactly

  const handleNavigateBack = () => {
    navigate("/missing-articles");
  };

  //   const handleImageClick = (imageSrc: string) => {
  //     setModalImageUrl(imageSrc);
  //     imageModalRef.current?.showModal();
  //   };

  const handlePromptModal = (prompt: string) => {
    setPromptModalContent(prompt);
    setIsPromptModalOpen(true);
  };

  const handleGenerateClick = () => {
    setIsGenerateModalOpen(true);
    setActiveTab("generated");
  };

  const handleGenerateSubmit = async () => {
    if (!itemId) return;

    // AI_GOOD: Reset all streaming state
    setGenerationError(null);
    setGenerationSuccessMessage(null);
    setGenerationWarningMessage(null);
    setGenerationUserMessage(null);
    setStreamingContent("");
    setStreamingThoughts("");
    setIsGenerating(true);
    setIsStreamingToArticleArea(true);
    setIsThoughtsAccordionOpen(true);
    setIsThinkingComplete(false);
    setIsGenerateModalOpen(false);

    const currentUserPrompt = getCurrentUserPrompt();

    try {
      await generationService.streamGenerate(
        itemId,
        selectedGenLanguage,
        currentUserPrompt,
        uploadedFiles,
        enableUrlContext,
        enableGoogleSearch,
        // AI_GOOD: Content chunk handler
        (text: string) => {
          setStreamingContent((prev) => prev + text);
        },
        // AI_GOOD: Thought chunk handler
        (text: string) => {
          setStreamingThoughts((prev) => {
            const thoughtText = text.trim();
            if (prev.endsWith(thoughtText)) return prev;
            const separator = prev && !prev.endsWith("\n") ? "\n" : "";
            return prev + separator + thoughtText + "\n";
          });
        },
        // AI_GOOD: Error handler
        (error: string) => {
          setGenerationError(error);
          setIsGenerating(false);
          setIsStreamingToArticleArea(false);
        },
        // AI_GOOD: Success handler
        (message: string) => {
          setGenerationSuccessMessage(message);
          setIsGenerating(false);
          setIsStreamingToArticleArea(false);
          setIsThinkingComplete(true);
          // Note: Don't fetch article details yet - wait for WebSocket completion notification
          // The HTTP stream completes before database saving in the new architecture
        },
        // AI_GOOD: Warning handler
        (message: string) => {
          setGenerationWarningMessage(message);
          setIsGenerating(false);
          setIsStreamingToArticleArea(false);
          setIsThinkingComplete(true);
          // Note: Don't fetch article details yet - wait for WebSocket completion notification
          // The HTTP stream completes before database saving in the new architecture
        },
      );

      // AI_GOOD: Reset form state after successful submission
      setUserPromptAddition("");
      setUploadedFiles([]);
      setEnableUrlContext(false);
      setEnableGoogleSearch(false);
    } catch (error) {
      console.error("Generation failed:", error);
      setGenerationError("Failed to start generation");
      setIsGenerating(false);
      setIsStreamingToArticleArea(false);
    }
  };

  const handleTranslateRequest = async (
    lang: string,
    version: number,
    fromLang: string,
    content: string,
  ) => {
    if (!itemId) return;

    // AI_GOOD: Reset all streaming state for translation
    setGenerationError(null); // Reuse generation states for translation stream
    setGenerationSuccessMessage(null);
    setGenerationWarningMessage(null);
    setGenerationUserMessage(null); // Reuse user message for info/progress
    setStreamingContent("");
    setStreamingThoughts("");
    setIsGenerating(true);
    setIsStreamingToArticleArea(true); // Show content area for streaming
    setIsThoughtsAccordionOpen(true); // Open thoughts area by default for streaming
    setIsThinkingComplete(false); // Reset thinking complete state

    const translationKey = `${version}_${lang}`;
    setPendingTranslations((prev) => ({ ...prev, [translationKey]: true }));

    try {
      // Use the refactored streaming translation service method
      await translationService.translateContent(
        itemId,
        version,
        fromLang,
        lang,
        content,
        enableUrlContext, // Pass flags from generation modal state (if UI exists)
        enableGoogleSearch, // Pass flags from generation modal state (if UI exists)
        // Callbacks to update frontend states
        (text: string) => {
          // onContentChunk
          setStreamingContent((prev) => prev + text);
        },
        (text: string) => {
          // onThoughtChunk
          setStreamingThoughts((prev) => {
            // This logic is duplicated from handleGenerateSubmit - consider abstracting
            const thoughtText = text.trim();
            if (prev.endsWith(thoughtText)) return prev; // Avoid exact duplicates
            const separator = prev && !prev.endsWith("\n") ? "\n" : ""; // Ensure separation with newline if needed
            return prev + separator + thoughtText + "\n"; // Append a standard newline for markdown
          });
        },
        (error: string) => {
          // onError
          setGenerationError(error);
          setIsGenerating(false);
          setIsStreamingToArticleArea(false);
          setIsThinkingComplete(true); // End thinking on error
          setGenerationUserMessage(null); // Clear any info message
          setPendingTranslations((prev) => ({
            ...prev,
            [translationKey]: false,
          })); // Reset pending state on error
        },
        (message: string, completedVersion?: number) => {
          // onSuccess
          setGenerationSuccessMessage(message); // Display success message
          setIsGenerating(false);
          setIsStreamingToArticleArea(false); // Hide streaming area after completion
          setIsThinkingComplete(true); // Mark thinking complete
          setGenerationUserMessage(null); // Clear any info message

          // Reset pending state on success
          setPendingTranslations((prev) => ({
            ...prev,
            [translationKey]: false,
          }));

          // Optional: Fetch updated data after successful save
          // The WebSocket notification should trigger this, but a fetch here can be a fallback
          // or provide a quicker update depending on WebSocket latency.
          // Ensure fetchArticleDetailsData handles preserving user selections appropriately.
          if (completedVersion !== undefined) {
            fetchArticleDetailsData().then(() => {
              setActiveTab("generated"); // Stay on generated tab
              // Maybe automatically select the newly translated language/version?
              // This might require parsing the success message or getting info from BE.
              // For now, rely on fetchArticleDetailsData refreshing data.
            });
          }
        },
        (message: string) => {
          // onInfo - reuse generationUserMessage state
          setGenerationUserMessage(message);
        },
      );

      // Pending translation state is handled by callbacks now, not a finally block here
      // Consider if setPendingTranslations needs to be managed by the streaming callbacks.
      // For now, let's remove the finally block that sets it to false.
    } catch (error) {
      console.error("Error initiating streaming translation:", error);
      setGenerationError("Failed to start streaming translation");
      setIsGenerating(false);
      setIsStreamingToArticleArea(false);
      setIsThinkingComplete(true); // End thinking on initiation error
      setGenerationUserMessage(null); // Clear any info message
      setPendingTranslations((prev) => ({ ...prev, [translationKey]: false })); // Clear pending state on initiation error
    }
  };

  // AI_GOOD: Rendering logic preserved exactly

  if (loading) {
    return <LoadingSpinner message="Loading article details..." />;
  }

  if (error) {
    return <ErrorDisplay error={error} onBack={handleNavigateBack} />;
  }

  if (!articleDetails) {
    return (
      <ErrorDisplay error="Article not found" onBack={handleNavigateBack} />
    );
  }

  return (
    <div className="container mx-auto max-w-7xl p-4">
      {/* AI_GOOD: Header preserved exactly */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={handleNavigateBack}
          className="btn btn-ghost btn-sm md:btn-md"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Missing Articles
        </button>
      </div>

      {/* AI_GOOD: Title section with editing functionality */}
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {getSectionIcon(articleDetails.section_title)}
              <h1 className="text-2xl font-bold md:text-3xl">
                {titleOverride || articleDetails.title || "Untitled Article"}
              </h1>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="btn btn-ghost btn-sm"
                title={isEditing ? "Cancel editing" : "Edit title"}
              >
                {isEditing ? (
                  <X className="h-4 w-4" />
                ) : (
                  <Edit className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* AI_GOOD: Title editor with animation */}
            <AnimatePresence>
              {renderEditor && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{
                    opacity: animateEditorVisible ? 1 : 0,
                    height: animateEditorVisible ? "auto" : 0,
                  }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 overflow-hidden"
                >
                  <div className="rounded-lg bg-base-100 p-4 shadow-lg">
                    <input
                      type="text"
                      className="input-bordered input w-full"
                      placeholder="Enter custom title..."
                      defaultValue={titleOverride || ""}
                      onKeyDown={async (e) => {
                        if (e.key === "Enter" && itemId) {
                          const newTitle = e.currentTarget.value.trim();
                          if (newTitle) {
                            try {
                              await handleTitleOverride({
                                _id: itemId,
                                title_override: newTitle,
                              });
                              console.log("Title updated successfully!");
                            } catch (error) {
                              console.error("Failed to update title: ", error);
                            }
                          }
                        }
                      }}
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={async () => {
                          const input = document.querySelector(
                            'input[placeholder="Enter custom title..."]',
                          ) as HTMLInputElement;
                          const newTitle = input?.value.trim();
                          if (newTitle && itemId) {
                            try {
                              await handleTitleOverride({
                                _id: itemId,
                                title_override: newTitle,
                              });
                              console.log("Title updated successfully!");
                            } catch (error) {
                              console.error("Failed to update title: ", error);
                            }
                          }
                        }}
                      >
                        <Save className="mr-1 h-3 w-3" />
                        Save
                      </button>
                      {titleOverride && (
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={async () => {
                            if (itemId) {
                              try {
                                await handleTitleRevert({ _id: itemId });
                                console.log("Title reverted to original!");
                              } catch (error) {
                                console.error(
                                  "Failed to revert title: ",
                                  error,
                                );
                              }
                            }
                          }}
                        >
                          <RefreshCw className="mr-1 h-3 w-3" />
                          Revert
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* AI_GOOD: Tab navigation */}
      <div className="tabs-boxed mb-6 tabs w-fit gap-2 select-none">
        <button
          className={`badge cursor-pointer badge-sm px-3 py-4 font-mono font-semibold transition-all duration-300 ${activeTab === "details" ? "text-primary-content badge-primary" : "border-neutral/20 outline-neutral/20 hover:bg-neutral/20"}`}
          onClick={() => setActiveTab("details")}
        >
          <Info className="mr-2 h-4 w-4" />
          Details
        </button>
        <button
          className={`badge cursor-pointer badge-sm px-3 py-4 font-mono font-semibold transition-all duration-300 ${activeTab === "generated" ? "text-primary-content badge-primary" : "border-neutral/20 outline-neutral/20 hover:bg-neutral/20"}`}
          onClick={() => setActiveTab("generated")}
        >
          <FileText className="mr-2 h-4 w-4" />
          Generated Content
        </button>
      </div>

      {/* AI_GOOD: Tab content */}
      {activeTab === "details" && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* AI_GOOD: Article details card */}
          <div className="card bg-base-100 shadow-lg">
            <div className="card-body">
              <h2 className="card-title text-lg">Article Details</h2>
              <dl className="space-y-1">
                <DetailItem
                  label="Original Title"
                  value={articleDetails.title}
                  icon={<BookOpen />}
                />
                <DetailItem
                  label="Description"
                  value={articleDetails.description}
                  icon={<BookOpen />}
                />
                <DetailItem
                  label="Section"
                  value={articleDetails.section_title}
                  icon={<Tag />}
                />
                <DetailItem
                  label="External URL"
                  value={articleDetails.release_item_url}
                  link={articleDetails.release_item_url}
                  icon={<ExternalLink />}
                />
              </dl>
            </div>
          </div>

          {/* AI_GOOD: Status management card */}
          <div className="card bg-base-100 shadow-lg">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <h2 className="card-title text-lg">Status & Notes</h2>
                <button
                  onClick={() => setIsEditingStatus(!isEditingStatus)}
                  className="btn btn-ghost btn-sm"
                >
                  {isEditingStatus ? (
                    <X className="h-4 w-4" />
                  ) : (
                    <Edit className="h-4 w-4" />
                  )}
                </button>
              </div>

              {isEditingStatus ? (
                <div className="space-y-4">
                  <div>
                    <label className="label">
                      <span className="label-text">Status</span>
                    </label>
                    <select
                      className="select-bordered select w-full"
                      value={editableUserStatus}
                      onChange={(e) =>
                        setEditableUserStatus(e.target.value as UserStatus)
                      }
                    >
                      <option value="Open">Open</option>
                      <option value="Completed">Completed</option>
                      <option value="Investigating">Investigating</option>
                      <option value="Not Relevant">Not Relevant</option>
                      <option value="Duplicate">Duplicate</option>
                      <option value="AI Generated">AI Generated</option>
                    </select>
                  </div>

                  <div>
                    <label className="label">
                      <span className="label-text">Notes</span>
                    </label>
                    <textarea
                      className="textarea-bordered textarea w-full"
                      rows={3}
                      value={editableUserNotes}
                      onChange={(e) => setEditableUserNotes(e.target.value)}
                      placeholder="Add notes about this article..."
                    />
                  </div>

                  {statusUpdateError && (
                    <div className="alert alert-error">
                      <AlertTriangle className="h-4 w-4" />
                      <span>{statusUpdateError}</span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={handleStatusUpdate}
                      disabled={isUpdatingStatus}
                      className="btn btn-sm btn-primary"
                    >
                      {isUpdatingStatus ? (
                        <>
                          <span className="loading loading-xs loading-spinner"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="mr-1 h-3 w-3" />
                          Save
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingStatus(false);
                        setEditableUserStatus(
                          articleDetails.user_status || "Open",
                        );
                        setEditableUserNotes(articleDetails.user_notes || "");
                        setStatusUpdateError(null);
                      }}
                      className="btn btn-outline btn-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Status:
                    </span>
                    <div
                      className={`ml-2 badge ${
                        editableUserStatus === "Completed"
                          ? "badge-success"
                          : editableUserStatus === "Investigating"
                            ? "badge-warning"
                            : editableUserStatus === "Not Relevant"
                              ? "badge-neutral"
                              : editableUserStatus === "Duplicate"
                                ? "badge-neutral"
                                : editableUserStatus === "AI Generated"
                                  ? "badge-info"
                                  : "badge-ghost"
                      }`}
                    >
                      {editableUserStatus}
                    </div>
                  </div>
                  {editableUserNotes && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Notes:
                      </span>
                      <p className="mt-1 text-sm">{editableUserNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* AI_GOOD: Generation button */}
          <div className="lg:col-span-2">
            <button
              onClick={handleGenerateClick}
              className="btn w-full btn-lg btn-primary"
            >
              <Plus className="mr-2 h-5 w-5" />
              Generate Article Content
            </button>
          </div>
        </div>
      )}

      {activeTab === "generated" && (
        <div className="space-y-6">
          {/* AI_GOOD: Generation controls */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                onClick={() => setIsGenerateModalOpen(true)}
                disabled={isGenerating}
                className="btn btn-primary"
              >
                <Plus className="mr-2 h-4 w-4" />
                {isGenerating ? "Generating..." : "Generate New Version"}
              </button>
            </div>

            {/* AI_GOOD: Version and language selectors */}
            <div className="flex flex-col gap-2">
              <VersionSelector
                allUniqueVersionsSorted={allUniqueVersionsSorted}
                selectedDisplayVersion={selectedDisplayVersion}
                onVersionSelect={setSelectedDisplayVersion}
              />
              <LanguageSelector
                selectedDisplayVersion={selectedDisplayVersion}
                selectedDisplayLanguage={selectedDisplayLanguage}
                currentDisplayDoc={currentDisplayDoc}
                pendingTranslations={pendingTranslations}
                isLanguageEnabledForVersion={isLanguageEnabledForVersion}
                onLanguageSelect={setSelectedDisplayLanguage}
                onTranslateRequest={handleTranslateRequest}
              />
            </div>
          </div>

          {/* AI_GOOD: Content display area */}
          <div className="grid gap-6 lg:grid-cols-3">
            {/* AI_GOOD: Main content area */}
            <div className="lg:col-span-2">
              <div className="card bg-base-100 shadow-lg">
                <div className="card-body">
                  {isStreamingToArticleArea ? (
                    <StreamingContentDisplay
                      streamingContent={streamingContent}
                      isStreamingToArticleArea={isStreamingToArticleArea}
                      markdownProps={defaultMarkdownProps}
                      currentThinkingSection={currentThinkingSection}
                    />
                  ) : selectedDisplayVersion !== null && currentDisplayDoc ? (
                    <>
                      <ContentDisplayHeader
                        selectedDisplayVersion={selectedDisplayVersion}
                        selectedDisplayLanguage={selectedDisplayLanguage}
                        currentDisplayDoc={currentDisplayDoc}
                        onViewPrompt={handlePromptModal}
                      />
                      <div className="divider my-2"></div>
                      <div className="animated-content-section prose prose-sm max-w-none prose-h1:text-2xl prose-h1:font-bold prose-h2:text-xl">
                        <ReactMarkdown {...defaultMarkdownProps}>
                          {currentDisplayDoc.content || "No content available."}
                        </ReactMarkdown>
                      </div>
                    </>
                  ) : (
                    <div className="flex h-64 items-center justify-center text-gray-500">
                      <div className="text-center">
                        <FileText className="mx-auto h-12 w-12 text-gray-300" />
                        <p className="mt-2">No content available</p>
                        <p className="text-sm">
                          Generate content to see it here
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* AI_GOOD: Thoughts sidebar */}
            <div className="lg:col-span-1">
              <div className="card bg-base-100 shadow-lg">
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <h3 className="card-title text-base">AI Thoughts</h3>
                    <button
                      onClick={() =>
                        setIsThoughtsAccordionOpen(!isThoughtsAccordionOpen)
                      }
                      className="btn btn-ghost btn-sm"
                    >
                      {isThoughtsAccordionOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </div>

                  <AnimatePresence>
                    {isThoughtsAccordionOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="divider my-2"></div>
                        <div className="animated-thought">
                          <StreamingThoughtsDisplay
                            streamingThoughts={streamingThoughts}
                            currentDisplayDoc={currentDisplayDoc}
                            isStreamingToArticleArea={isStreamingToArticleArea}
                            isGenerating={isGenerating}
                            markdownProps={defaultMarkdownProps}
                            currentThinkingSection={currentThinkingSection}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI_GOOD: Generation modal */}
      {isGenerateModalOpen && (
        <div className="modal-open modal">
          <div className="modal-box max-w-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Generate Article Content</h3>
              <button
                onClick={() => setIsGenerateModalOpen(false)}
                className="btn btn-ghost btn-sm"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 py-4">
              {/* AI_GOOD: Language selection */}
              <div>
                <label className="label">
                  <span className="label-text">Target Language</span>
                </label>
                <div className="flex gap-2">
                  {ALL_POSSIBLE_LANGUAGES.map((lang: string) => (
                    <button
                      key={lang}
                      onClick={() => setSelectedGenLanguage(lang)}
                      className={`btn btn-sm ${
                        selectedGenLanguage === lang
                          ? "btn-primary"
                          : "btn-outline"
                      }`}
                    >
                      {languageMap[lang]?.flagComponent}
                      {languageMap[lang]?.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* AI_GOOD: Additional prompt */}
              <div>
                <label className="label">
                  <span className="label-text">
                    Additional Instructions (Optional)
                  </span>
                </label>
                <textarea
                  ref={userPromptRef}
                  className="textarea-bordered textarea w-full"
                  rows={3}
                  placeholder="Add specific instructions for the AI..."
                  onChange={debouncedSyncHandler}
                />
              </div>

              {/* AI_GOOD: File uploads */}
              <div>
                <label className="label">
                  <span className="label-text">Upload Files (Optional)</span>
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  accept="image/*,video/*,.pdf,.txt,.doc,.docx,.mp4,.mov,.avi,.mkv,.xls,.xlsx,.ppt,.pptx"
                  onChange={handleFileChange}
                  className="file-input-bordered file-input w-full"
                />
                {uploadedFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {uploadedFiles.map((file) => (
                      <FileUploadItem
                        key={file.id}
                        file={file}
                        onRemove={removeUploadedFile}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* AI_GOOD: Enhancement options */}
              <div className="space-y-2">
                <div className="form-control">
                  <label className="label cursor-pointer">
                    <span className="label-text flex items-center">
                      <Globe className="mr-2 h-4 w-4" />
                      Enable URL Context
                    </span>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={enableUrlContext}
                      onChange={(e) => setEnableUrlContext(e.target.checked)}
                    />
                  </label>
                </div>
                <div className="form-control">
                  <label className="label cursor-pointer">
                    <span className="label-text flex items-center">
                      <Search className="mr-2 h-4 w-4" />
                      Enable Google Search
                    </span>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={enableGoogleSearch}
                      onChange={(e) => setEnableGoogleSearch(e.target.checked)}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-action">
              <button
                onClick={() => setIsGenerateModalOpen(false)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateSubmit}
                disabled={isGenerating}
                className="btn btn-primary"
              >
                {isGenerating ? (
                  <>
                    <span className="loading loading-sm loading-spinner"></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI_GOOD: Prompt modal */}
      {isPromptModalOpen && (
        <div className="modal-open modal">
          <div className="modal-box max-w-3xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Generation Prompt</h3>
              <button
                onClick={() => setIsPromptModalOpen(false)}
                className="btn btn-ghost btn-sm"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="py-4">
              <div className="rounded-lg bg-base-200 p-4">
                <pre className="text-sm whitespace-pre-wrap">
                  {promptModalContent}
                </pre>
              </div>
            </div>
            <div className="modal-action">
              <button
                onClick={() => setIsPromptModalOpen(false)}
                className="btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI_GOOD: Image modal */}
      <dialog ref={imageModalRef} className="modal">
        <div className="modal-box max-w-4xl">
          <div className="flex justify-end">
            <button
              onClick={() => imageModalRef.current?.close()}
              className="btn btn-ghost btn-sm"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {modalImageUrl && (
            <img
              src={modalImageUrl}
              alt="Enlarged view"
              className="h-auto w-full"
            />
          )}
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    </div>
  );
};

export default MissingArticle;
